import React, { useState, useEffect } from 'react';
import { AppSettings, AudioDevice } from '@/types';
import { audioService } from '@/services/audioService';
import { AudioMeter } from '@/components/AudioMeter';
import {
  Sparkles,
  ShieldCheck,
  Folder,
  Mic,
  Monitor,
  Volume2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

interface OnboardingScreenProps {
  settings: AppSettings;
  onUpdateSettings: (settings: Partial<AppSettings>) => void;
  onComplete: () => void;
}

export const OnboardingScreen: React.FC<OnboardingScreenProps> = ({
  settings,
  onUpdateSettings,
  onComplete
}) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [folderPath, setFolderPath] = useState(settings.storagePath);
  const [micDevices, setMicDevices] = useState<AudioDevice[]>([]);
  const [selectedMic, setSelectedMic] = useState(settings.defaultMicId);
  const [screenPermissionOk, setScreenPermissionOk] = useState(false);
  const [micPermissionOk, setMicPermissionOk] = useState(false);
  const [isTestingMic, setIsTestingMic] = useState(false);
  const [testMicStream, setTestMicStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    audioService.getAudioInputDevices().then(devices => {
      setMicDevices(devices);
      if (devices.length > 0 && selectedMic === 'default') {
        setSelectedMic(devices[0].deviceId);
      }
    });
  }, [selectedMic]);

  const handleSelectFolder = async () => {
    if (window.electronAPI && typeof window.electronAPI.selectDirectory === 'function') {
      const selected = await window.electronAPI.selectDirectory(folderPath);
      if (selected) {
        setFolderPath(selected);
        onUpdateSettings({ storagePath: selected });
      }
    } else {
      // Prompt fallback for web mode
      const entered = window.prompt('Enter local storage directory path:', folderPath);
      if (entered) {
        setFolderPath(entered);
        onUpdateSettings({ storagePath: entered });
      }
    }
  };

  const handleTestScreenPermission = async () => {
    try {
      if (window.electronAPI) {
        const sources = await window.electronAPI.getSources();
        setScreenPermissionOk(sources.length > 0);
      } else {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        stream.getTracks().forEach(t => t.stop());
        setScreenPermissionOk(true);
      }
    } catch {
      setScreenPermissionOk(false);
    }
  };

  const handleTestMic = async () => {
    if (isTestingMic) {
      if (testMicStream) {
        testMicStream.getTracks().forEach(t => t.stop());
      }
      audioService.stopMeters();
      setIsTestingMic(false);
      return;
    }

    try {
      const stream = await audioService.getMicrophoneStream(selectedMic);
      if (stream) {
        setTestMicStream(stream);
        audioService.initMeters(stream, null);
        setIsTestingMic(true);
        setMicPermissionOk(true);
      }
    } catch {
      setMicPermissionOk(false);
    }
  };

  useEffect(() => {
    return () => {
      if (testMicStream) {
        testMicStream.getTracks().forEach(t => t.stop());
      }
      audioService.stopMeters();
    };
  }, [testMicStream]);

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-12 flex justify-center items-center ambient-gradient">
      <div className="max-w-2xl w-full bg-brand-surface border border-brand-border rounded-3xl p-8 md:p-10 shadow-elevated">
        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-brand-border/60">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step === s
                    ? 'bg-brand-accent text-white shadow-glow'
                    : step > s
                    ? 'bg-brand-secondary/20 text-brand-secondary border border-brand-secondary/40'
                    : 'bg-brand-elevated text-brand-muted border border-brand-border'
                }`}
              >
                {step > s ? '✓' : s}
              </div>
            ))}
          </div>
          <span className="text-xs text-brand-muted font-medium">Step {step} of 4</span>
        </div>

        {/* STEP 1: Welcome & Value Proposition */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-glow mb-4 ring-1 ring-brand-border/60 shrink-0 bg-brand-surface flex items-center justify-center">
              <img
                src="./app-icon.png"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.fallback-logo')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'fallback-logo w-full h-full bg-gradient-to-tr from-purple-600 to-indigo-500 flex items-center justify-center text-white font-black text-xl select-none';
                    fallback.innerText = 'M';
                    parent.appendChild(fallback);
                  }
                }}
                alt="MeetSplit AI Logo"
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-brand-text tracking-tight">
                Welcome to MeetSplit AI
              </h1>
              <p className="text-brand-secondary font-medium text-sm mt-1">
                Record Once. Split Every Speaker.
              </p>
            </div>

            <p className="text-sm text-brand-muted leading-relaxed">
              MeetSplit AI is a privacy-first desktop application designed for video conferences and online meetings.
              It captures your meeting, analyzes video frames locally on your computer, and extracts isolated participant video clips with zero cloud uploads.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="p-4 rounded-xl bg-brand-elevated/60 border border-brand-border flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-semibold text-brand-text">100% Offline & Private</h4>
                  <p className="text-[11px] text-brand-muted mt-0.5">Media stays on your disk. No remote AI APIs or cloud subscriptions.</p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-brand-elevated/60 border border-brand-border flex items-start gap-3">
                <Monitor className="w-5 h-5 text-brand-accent shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-semibold text-brand-text">Local Participant Separation</h4>
                  <p className="text-[11px] text-brand-muted mt-0.5">Automatic tile detection with manual crop verification.</p>
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button
                onClick={() => setStep(2)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-sm font-semibold shadow-glow transition-all active:scale-95"
              >
                <span>Start Setup</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Storage Folder Setup */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-brand-text">Configure Local Storage Folder</h2>
              <p className="text-xs text-brand-muted mt-1">
                All recordings, project manifests, and participant MP4 exports will be stored in this directory.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-brand-elevated/60 border border-brand-border space-y-3">
              <label className="text-xs font-medium text-brand-muted flex items-center gap-2">
                <Folder className="w-4 h-4 text-brand-accent" />
                Recording Destination Directory
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={folderPath}
                  onChange={(e) => {
                    setFolderPath(e.target.value);
                    onUpdateSettings({ storagePath: e.target.value });
                  }}
                  className="flex-1 bg-brand-surface border border-brand-border rounded-xl px-3.5 py-2 text-xs text-brand-text font-mono focus:outline-none focus:border-brand-accent"
                />
                <button
                  onClick={handleSelectFolder}
                  className="px-4 py-2 bg-brand-surface hover:bg-brand-surface/80 border border-brand-border rounded-xl text-xs font-medium text-brand-text transition-colors"
                >
                  Browse...
                </button>
              </div>
              <p className="text-[11px] text-brand-muted flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                Direct local storage ensures no external network bandwidth is consumed.
              </p>
            </div>

            <div className="pt-4 flex items-center justify-between">
              <button
                onClick={() => setStep(1)}
                className="text-xs text-brand-muted hover:text-brand-text px-3 py-2"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-sm font-semibold shadow-glow transition-all active:scale-95"
              >
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Permissions & Hardware Verification */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-brand-text">Hardware Permissions Checklist</h2>
              <p className="text-xs text-brand-muted mt-1">
                Verify screen capture and audio device permissions for reliable recording.
              </p>
            </div>

            <div className="space-y-3">
              {/* Screen capture permission check */}
              <div className="p-4 rounded-2xl bg-brand-elevated/60 border border-brand-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-brand-surface border border-brand-border">
                    <Monitor className="w-4 h-4 text-brand-accent" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-brand-text">Display & Window Capture</h4>
                    <p className="text-[11px] text-brand-muted">Permission to record monitors and application windows</p>
                  </div>
                </div>

                <button
                  onClick={handleTestScreenPermission}
                  className={`text-xs px-3 py-1.5 rounded-lg border font-medium flex items-center gap-1.5 transition-colors ${
                    screenPermissionOk
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                      : 'bg-brand-surface hover:bg-brand-surface/80 text-brand-text border-brand-border'
                  }`}
                >
                  {screenPermissionOk ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
                  {screenPermissionOk ? 'Verified' : 'Test Capture'}
                </button>
              </div>

              {/* Microphone & System Audio check */}
              <div className="p-4 rounded-2xl bg-brand-elevated/60 border border-brand-border space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-brand-surface border border-brand-border">
                      <Mic className="w-4 h-4 text-brand-secondary" />
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-brand-text">Microphone Input</h4>
                      <p className="text-[11px] text-brand-muted">Captures your voice during the call</p>
                    </div>
                  </div>

                  <button
                    onClick={handleTestMic}
                    className={`text-xs px-3 py-1.5 rounded-lg border font-medium flex items-center gap-1.5 transition-colors ${
                      isTestingMic
                        ? 'bg-brand-recording text-white border-brand-recording animate-pulse'
                        : micPermissionOk
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                        : 'bg-brand-surface hover:bg-brand-surface/80 text-brand-text border-brand-border'
                    }`}
                  >
                    {isTestingMic ? 'Stop Test' : micPermissionOk ? 'Verified' : 'Test Microphone'}
                  </button>
                </div>

                {/* Device Selector */}
                <div className="pt-2">
                  <select
                    value={selectedMic}
                    onChange={(e) => {
                      setSelectedMic(e.target.value);
                      onUpdateSettings({ defaultMicId: e.target.value });
                    }}
                    className="w-full bg-brand-surface border border-brand-border rounded-xl px-3 py-2 text-xs text-brand-text focus:outline-none focus:border-brand-accent"
                  >
                    {micDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Live meter while testing */}
                {isTestingMic && (
                  <div className="pt-1">
                    <AudioMeter showLabels={true} systemAudioEnabled={false} />
                  </div>
                )}
              </div>
            </div>

            <div className="pt-4 flex items-center justify-between">
              <button
                onClick={() => setStep(2)}
                className="text-xs text-brand-muted hover:text-brand-text px-3 py-2"
              >
                Back
              </button>
              <button
                onClick={() => setStep(4)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-sm font-semibold shadow-glow transition-all active:scale-95"
              >
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: First-Recording Walkthrough */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-brand-text">Ready for Your First Recording!</h2>
              <p className="text-xs text-brand-muted mt-1">
                Quick 3-step walkthrough to get the best participant separation results:
              </p>
            </div>

            <div className="space-y-3">
              <div className="p-3.5 rounded-2xl bg-brand-elevated/60 border border-brand-border flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-accent/20 text-brand-accent font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                  1
                </span>
                <div>
                  <h4 className="text-xs font-semibold text-brand-text">Select Meeting Window or Monitor</h4>
                  <p className="text-[11px] text-brand-muted mt-0.5">
                    For optimal participant detection, select the monitor or browser window showing your meeting in Gallery / Grid View.
                  </p>
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-brand-elevated/60 border border-brand-border flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-accent/20 text-brand-accent font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                  2
                </span>
                <div>
                  <h4 className="text-xs font-semibold text-brand-text">Record and Stop</h4>
                  <p className="text-[11px] text-brand-muted mt-0.5">
                    Start recording when your meeting begins. When finished, hit Stop Recording to initiate local computer-vision processing.
                  </p>
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-brand-elevated/60 border border-brand-border flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-accent/20 text-brand-accent font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                  3
                </span>
                <div>
                  <h4 className="text-xs font-semibold text-brand-text">Review and Export</h4>
                  <p className="text-[11px] text-brand-muted mt-0.5">
                    Review candidate participant crops, adjust bounding boxes visually, rename speakers, and export individual MP4s with audio!
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 flex items-center justify-between">
              <button
                onClick={() => setStep(3)}
                className="text-xs text-brand-muted hover:text-brand-text px-3 py-2"
              >
                Back
              </button>
              <button
                onClick={onComplete}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-brand-accent to-brand-secondary text-white text-sm font-semibold shadow-glow transition-all active:scale-95"
              >
                <span>Launch Dashboard</span>
                <CheckCircle2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
